import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { message, Spin, Tag } from 'antd-v5';
import PageHeaderLayout from '@/component/page-header-layout';
import { Button, Card, Form, InputNumber, Modal, Table } from '@/components';
import {
  calculateDelegationCollectionRuleRequest,
  getDelegationCollectionRuleRequest,
  getDelegationCollectionRuleCalculateResultRequest,
  updateDelegationCollectionRuleRequest,
} from '@/state/request/delegationCollectionRule';
import { createEndValueRule, createIntegerRules, RATIO_MAX, RATIO_MIN } from './formRules';
import './style.less';

const CALCULATE_STATUS = {
  COMPLETED: 'COMPLETED',
  PROCESSING: 'PROCESSING',
};

const INITIAL_CALCULATE_INFO = {
  status: null,
  ruleItem: {},
};

const isCalculateRequestSuccess = (res) => res?.code === 200;

const normalizeCalculateResult = (payload) => ({
  ...INITIAL_CALCULATE_INFO,
  ...(payload || {}),
  ruleItem: payload?.ruleItem || {},
});

const formatUsageText = ({ todayCalculateCount, dayCalculateLimit } = {}) => {
  if (todayCalculateCount === undefined && dayCalculateLimit === undefined) return '';
  if (todayCalculateCount !== undefined && dayCalculateLimit !== undefined) {
    return `已用 ${todayCalculateCount}/${dayCalculateLimit} 次`;
  }
  if (todayCalculateCount !== undefined) return `已用 ${todayCalculateCount} 次`;
  return `每日 ${dayCalculateLimit} 次`;
};

const formatAmount = (amount) => {
  if (amount === undefined || amount === null || amount === '') return '--';
  const numberAmount = Number(amount);
  if (!Number.isFinite(numberAmount)) return amount;
  return `¥${numberAmount.toLocaleString()}`;
};

const formatUpdateTime = (time) => (time ? moment(time).format('YYYY-MM-DD HH:mm:ss') : '--');

const isReachedMaxCount = ({ todayCalculateCount, dayCalculateLimit } = {}) =>
  Number.isFinite(Number(todayCalculateCount)) &&
  Number.isFinite(Number(dayCalculateLimit)) &&
  Number(todayCalculateCount) >= Number(dayCalculateLimit);

const scrollPageToTop = () => {
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  const layoutContent = document.querySelector('.ant-layout-content');
  if (layoutContent) {
    layoutContent.scrollTop = 0;
  }
};

const DelegationCollectionRuleSetting = ({ history }) => {
  const [calculateForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [calculateVisible, setCalculateVisible] = useState(false);
  const [calculateResultLoading, setCalculateResultLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [calculateInfo, setCalculateInfo] = useState(INITIAL_CALCULATE_INFO);

  const getRuleList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDelegationCollectionRuleRequest();
      if (res?.code === 200) {
        setDataSource(Array.isArray(res.data) ? res.data : []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    scrollPageToTop();
    getRuleList();
  }, [getRuleList]);

  const handleToggle = async (record) => {
    setUpdatingId(record.id);
    try {
      const res = await updateDelegationCollectionRuleRequest({
        id: record.id,
        enable: !record.enable,
      });
      if (res?.code === 200) {
        message.success(`${record.enable ? '停用' : '启用'}成功`);
        await getRuleList();
      }
    } finally {
      setUpdatingId(null);
    }
  };

  const fillCalculateForm = (ruleItem) => {
    calculateForm.resetFields();
    calculateForm.setFieldsValue(ruleItem || {});
  };

  const handleOpenCalculate = async () => {
    setCalculateInfo(INITIAL_CALCULATE_INFO);
    calculateForm.resetFields();
    setCalculateVisible(true);
    setCalculateResultLoading(true);
    try {
      const res = await getDelegationCollectionRuleCalculateResultRequest();
      if (isCalculateRequestSuccess(res)) {
        const nextInfo = normalizeCalculateResult(res.data);
        setCalculateInfo(nextInfo);
        fillCalculateForm(nextInfo.ruleItem);
      }
    } finally {
      setCalculateResultLoading(false);
    }
  };

  const handleCloseCalculate = () => {
    setCalculateVisible(false);
  };

  const handleQueryCalculate = async () => {
    if (isReachedMaxCount(calculateInfo)) {
      message.warning(`今日已使用${calculateInfo.todayCalculateCount}次，请明日再来`);
      return;
    }

    try {
      const values = await calculateForm.validateFields();
      const params = {
        ...values,
        tradeDayEnd: values.tradeDayEnd ?? null,
        creditAmountEnd: values.creditAmountEnd ?? null,
      };
      setCalculating(true);
      const res = await calculateDelegationCollectionRuleRequest(params);
      if (isCalculateRequestSuccess(res) && res.data === true) {
        setCalculateInfo((prevInfo) => ({
          ...prevInfo,
          status: CALCULATE_STATUS.PROCESSING,
          todayCalculateCount:
            prevInfo.todayCalculateCount === undefined
              ? prevInfo.todayCalculateCount
              : Number(prevInfo.todayCalculateCount) + 1,
          buyerCount: 0,
          totalCreditAmount: 0,
          updateTime: null,
          ruleItem: params,
        }));
      }
    } catch (error) {
      // 表单校验失败时保持弹窗现状
    } finally {
      setCalculating(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: '序号',
        key: 'index',
        width: 72,
        render: (_, __, index) => index + 1,
      },
      {
        title: '佣金比例',
        dataIndex: 'delegationCollectionRuleItemVO',
        key: 'ratio',
        render: (rule) => <span className='delegation-rule-ratio'>{rule?.ratio ?? 0}%</span>,
      },
      {
        title: '状态',
        dataIndex: 'enable',
        key: 'enable',
        render: (enable) =>
          enable ? (
            <Tag color='success'>启用</Tag>
          ) : (
            <Tag className='delegation-rule-disabled'>停用</Tag>
          ),
      },
      {
        title: '操作',
        key: 'action',
        width: 180,
        render: (_, record) => (
          <div className='delegation-rule-actions'>
            <button
              type='button'
              onClick={() =>
                history.push(`/setting/delegationCollectionRuleSetting/edit/${record.id}`)
              }
            >
              编辑
            </button>
            <button
              type='button'
              disabled={updatingId === record.id}
              onClick={() => handleToggle(record)}
            >
              {record.enable ? '停用' : '启用'}
            </button>
          </div>
        ),
      },
    ],
    [history, updatingId],
  );

  const isCalculateProcessing = calculateInfo.status === CALCULATE_STATUS.PROCESSING;
  const isCalculateCompleted = calculateInfo.status === CALCULATE_STATUS.COMPLETED;
  const usageText = formatUsageText(calculateInfo);

  return (
    <PageHeaderLayout>
      <Card bordered={false}>
        <div className='delegation-rule-toolbar'>
          <Button
            className='delegation-rule-calculate-button'
            loading={calculateResultLoading}
            onClick={handleOpenCalculate}
          >
            测算
          </Button>
        </div>
        <Table
          rowKey='id'
          loading={loading}
          columns={columns}
          dataSource={dataSource}
          pagination={false}
        />
      </Card>
      <Modal
        title={null}
        open={calculateVisible}
        width={600}
        footer={null}
        closable={false}
        destroyOnClose
        forceRender
        maskClosable={false}
        onCancel={handleCloseCalculate}
        wrapClassName='delegation-calculate-modal-wrap'
      >
        <Spin spinning={calculateResultLoading}>
          <div className='delegation-calculate-content'>
            <div className='delegation-calculate-header'>
              <span className='delegation-calculate-title'>测算</span>
              {usageText ? <span className='delegation-calculate-usage'>{usageText}</span> : null}
            </div>
            <Form
              form={calculateForm}
              className='delegation-calculate-form'
              labelCol={{ flex: '100px' }}
              wrapperCol={{ flex: 1 }}
              requiredMark={false}
              labelAlign='left'
            >
              <Form.Item
                name='ratio'
                label='佣金比例'
                rules={createIntegerRules('佣金比例', { min: RATIO_MIN, max: RATIO_MAX })}
              >
                <InputNumber
                  min={RATIO_MIN}
                  max={RATIO_MAX}
                  precision={0}
                  addonAfter='%'
                  disabled={isCalculateProcessing}
                />
              </Form.Item>
              <Form.Item label='未交易天数' className='delegation-calculate-range-row'>
                <Form.Item
                  name='tradeDayStart'
                  rules={createIntegerRules('未交易天数起始值')}
                  noStyle
                >
                  <InputNumber min={0} precision={0} disabled={isCalculateProcessing} />
                </Form.Item>
                <span className='delegation-rule-separator'>~</span>
                <Form.Item
                  name='tradeDayEnd'
                  dependencies={['tradeDayStart']}
                  rules={[
                    ...createIntegerRules('未交易天数结束值', { required: false }),
                    createEndValueRule('tradeDayStart', '未交易天数结束值'),
                  ]}
                  noStyle
                >
                  <InputNumber
                    min={0}
                    precision={0}
                    placeholder='不限'
                    disabled={isCalculateProcessing}
                  />
                </Form.Item>
                <span className='delegation-rule-unit'>天</span>
              </Form.Item>
              <Form.Item label='赊欠金额' className='delegation-calculate-range-row'>
                <Form.Item
                  name='creditAmountStart'
                  rules={createIntegerRules('赊欠金额起始值')}
                  noStyle
                >
                  <InputNumber min={0} precision={0} disabled={isCalculateProcessing} />
                </Form.Item>
                <span className='delegation-rule-separator'>~</span>
                <Form.Item
                  name='creditAmountEnd'
                  dependencies={['creditAmountStart']}
                  rules={[
                    ...createIntegerRules('赊欠金额结束值', { required: false }),
                    createEndValueRule('creditAmountStart', '赊欠金额结束值'),
                  ]}
                  noStyle
                >
                  <InputNumber
                    min={0}
                    precision={0}
                    placeholder='不限'
                    disabled={isCalculateProcessing}
                  />
                </Form.Item>
                <span className='delegation-rule-unit'>元</span>
              </Form.Item>
            </Form>

            {isCalculateProcessing ? (
              <div className='delegation-calculate-processing'>测算中，请稍后查看结果</div>
            ) : isCalculateCompleted ? (
              <div className='delegation-calculate-result'>
                <div className='delegation-calculate-time'>
                  更新时间：{formatUpdateTime(calculateInfo.updateTime)}
                </div>
                <div className='delegation-calculate-result-row'>
                  <span>
                    买家数：
                    <strong>{calculateInfo.buyerCount ?? '--'}</strong> 人
                  </span>
                  <span>
                    赊欠总额：
                    <strong>{formatAmount(calculateInfo.totalCreditAmount)}</strong>
                  </span>
                </div>
              </div>
            ) : null}

            <div className='delegation-calculate-footer'>
              <Button disabled={calculateResultLoading} onClick={handleCloseCalculate}>
                关闭
              </Button>
              <Button
                type='primary'
                loading={calculating}
                disabled={calculateResultLoading || isCalculateProcessing}
                onClick={handleQueryCalculate}
              >
                查询
              </Button>
            </div>
          </div>
        </Spin>
      </Modal>
    </PageHeaderLayout>
  );
};

export default DelegationCollectionRuleSetting;
